import React, { useEffect, useState } from 'react';
import { Button, Flex, Input, Text } from './base-components';

/** Seconds the resend button stays disabled, so one seller can't fire off ten texts. */
const RESEND_COOLDOWN_SECONDS = 30;

interface Props {
  /** Masked by AXS — "(XXX) XXX-5298". Sellers often have more than one AXS account. */
  phoneNumber?: string;
  /** Call 2 is in flight. */
  submitting: boolean;
  /** Call 1 is in flight again, for a fresh code. */
  resending: boolean;
  /**
   * Set once a code has been refused. The session that code belonged to is
   * spent either way, so the only way forward is a new code — the submit button
   * stays out until one arrives.
   */
  spent: boolean;
  error?: string;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onCancel: () => void;
}

/**
 * AXS now requires a one-time code for every transfer, and the token it mints
 * authorizes exactly one. This is the step between the two manual-transfer
 * calls: nothing has transferred while it is on screen.
 */
export const AxsTransferOtpPrompt: React.FC<Props> = ({
  phoneNumber,
  submitting,
  resending,
  spent,
  error,
  onSubmit,
  onResend,
  onCancel,
}) => {
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  // A refused code clears the box: the old one is burnt, and leaving it there
  // invites the seller to press submit again with the same digits.
  useEffect(() => {
    if (spent) setCode('');
  }, [spent]);

  // Restarts whenever a new code goes out, so the cooldown tracks the last text.
  useEffect(() => {
    if (resending) setCooldown(RESEND_COOLDOWN_SECONDS);
  }, [resending]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const busy = submitting || resending;
  const canSubmit = !busy && !spent && /^\d{6}$/.test(code);
  const canResend = !busy && cooldown <= 0;

  return (
    <div>
      <Text variant="heading" mb={2}>Enter your AXS verification code</Text>
      <Text variant="muted" mb={3}>
        AXS requires a code for every ticket transfer.
        {phoneNumber ? ` We texted one to ${phoneNumber}.` : ' We texted one to the phone on your AXS account.'}
      </Text>

      <Flex direction="col" mb={3}>
        <Input
          autoFocus
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          label="Verification code"
          placeholder="123456"
          value={code}
          disabled={busy || spent}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit) {
              e.preventDefault();
              onSubmit(code);
            }
          }}
          error={!!error}
          errorMessage={error}
        />
      </Flex>

      <div className="mb-4">
        <button
          type="button"
          onClick={onResend}
          disabled={!canResend}
          className="text-sm text-blue-400 hover:text-blue-300 transition-colors disabled:text-white/30 disabled:cursor-not-allowed"
        >
          {resending
            ? 'Sending a new code...'
            : cooldown > 0
              ? `Didn't get it? Send a new code (${cooldown}s)`
              : "Didn't get it? Send a new code"}
        </button>
      </div>

      <Flex justify="end" gap={2}>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => onSubmit(code)}
          disabled={!canSubmit}
        >
          {submitting ? 'Transferring...' : 'Complete transfer'}
        </Button>
      </Flex>
    </div>
  );
};
